import type { ZoomContactCenterIntegration } from "@repo/app-types/entities/integration";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { InfoBanner } from "@repo/ui/InfoBanner/infoBanner";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import { SimpleModalVariant } from "@repo/ui/Modal/modal";
import { useState, type FC } from "react";
import {
  saveZoomQueueIdsInOrder,
  useZoomContactCenterQueuesQuery,
} from "../../../api/entities/zoomContactCenterIntegration.js";
import { getApiErrorMessage } from "../../../api/helpers/errors.js";
import { useTeammateAuthorization } from "../../../helpers/teammateAuthorizationContext.js";
import { useAppDispatch } from "../../../hooks/hook.js";
import {
  formatQueueCount,
  ZoomContactCenterQueuePicker,
} from "./zoomContactCenterQueuePicker.js";

export interface ZoomContactCenterQueueSettingsProps {
  integration: ZoomContactCenterIntegration;
}

/**
 * The Zoom CC Settings tab's Queues section: the queue picker over the
 * connected account's live queue list, saving each change immediately.
 */
export const ZoomContactCenterQueueSettings: FC<
  ZoomContactCenterQueueSettingsProps
> = (props) => {
  const { integration } = props;
  const ability = useTeammateAuthorization();
  const canUpdate = ability.can(
    PermissionActions.UPDATE,
    PermissionSubjects.INTEGRATIONS,
  );
  const queuesQuery = useZoomContactCenterQueuesQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const dispatch = useAppDispatch();
  // Saves started by this mount only; the shared save queue may hold more.
  const [pendingSaves, setPendingSaves] = useState(0);
  const [saveError, setSaveError] = useState<string | null>(null);

  const storedIds = integration.settings.zoom_queue_ids ?? [];

  // Null imports every queue, including ones added in Zoom later; a list
  // imports exactly those queues.
  const saveQueueIds = (queueIds: readonly string[] | null) => {
    setSaveError(null);
    setPendingSaves((count) => count + 1);
    saveZoomQueueIdsInOrder(dispatch, {
      integrationId: integration.id,
      queueIds,
    })
      .catch((error: unknown) => {
        setSaveError(
          getApiErrorMessage(
            error,
            "Couldn't save the queue selection. Try again.",
          ),
        );
      })
      .finally(() => {
        setPendingSaves((count) => count - 1);
      });
  };

  return (
    <section className="border-t border-border-light pt-6">
      <h2 className="text-sm font-semibold text-foreground">Queues</h2>
      <p className="mt-1 max-w-2xl text-sm leading-5 text-muted-foreground">
        Choose which Zoom Contact Center queues to import call recordings from.
        Changes save as you make them and apply from the next sync; calls
        already imported stay, and calls from queues you add aren&apos;t
        backfilled.
      </p>

      {queuesQuery.isError ? (
        <div className="mt-4">
          <InfoBanner
            description={getApiErrorMessage(
              queuesQuery.error,
              "Couldn't load your queues from Zoom. Try again.",
            )}
            title="Couldn't load queues from Zoom"
            variant="CAUTION"
          />
          <p className="mt-3 text-sm text-muted-foreground">
            {storedIds.length > 0
              ? `Call recordings are imported from ${formatQueueCount(storedIds.length)}: ${storedIds.join(", ")}.`
              : "Call recordings from every queue are imported."}
          </p>
        </div>
      ) : null}

      {saveError ? (
        <div className="mt-4">
          <InfoBanner
            description={saveError}
            title="Couldn't save"
            variant={SimpleModalVariant.Warning}
          />
        </div>
      ) : null}

      {queuesQuery.isLoading ? (
        <div className="mt-4">
          <Loader style={LoaderStyle.SPINNER} />
        </div>
      ) : null}

      {queuesQuery.data ? (
        <ZoomContactCenterQueuePicker
          isDisabled={!canUpdate}
          isSaving={pendingSaves > 0}
          isTruncated={queuesQuery.data.is_truncated}
          minimumSelectionHint="At least one queue must stay selected. To stop importing, pause the sync."
          onChange={saveQueueIds}
          queueIds={integration.settings.zoom_queue_ids ?? null}
          zoomQueues={queuesQuery.data.queues}
        />
      ) : null}
    </section>
  );
};
